import { jsonInit } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import { statusCountsSchema } from '../../../lib/statusCounts';
import {
  publisherSchema,
  publishersPageSchema,
  serializePublisherListFilter,
  type PublisherCostPayload,
  type PublisherListFilter,
  type PublisherMutationPayload,
} from './model';

type PublisherClientOptions = {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
};

const publisherCostsResponseSchema = z.preprocess(
  (value) => (value === '' || value === null ? undefined : value),
  z.void(),
);
const publisherMutationResponseSchema = z.preprocess(
  (value) => (value === '' ? null : value),
  publisherSchema.nullable(),
);

export function createPublisherClient({ config, accessToken, fetcher }: PublisherClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);
  return {
    getStatusCounts: (searchTerm: string) =>
      api.request({
        path: `/api/publishers/statuses?${new URLSearchParams({ name: searchTerm.trim() })}`,
        schema: statusCountsSchema,
      }),
    list: (filter: PublisherListFilter, options?: { signal?: AbortSignal }) =>
      api.request({
        path: `/api/publishers?${serializePublisherListFilter(filter)}`,
        schema: publishersPageSchema,
        ...(options?.signal ? { init: { signal: options.signal } } : {}),
      }),
    get: (id: string) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(id)}`,
        schema: publisherSchema,
      }),
    create: (payload: PublisherMutationPayload) =>
      api.request({
        path: '/api/publishers',
        schema: publisherMutationResponseSchema,
        init: jsonInit('POST', payload),
      }),
    update: (id: string, payload: PublisherMutationPayload) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(id)}`,
        schema: publisherMutationResponseSchema,
        init: jsonInit('PUT', { ...payload, id }),
      }),
    updateCosts: (
      id: string,
      costs: {
        allProductsCost: PublisherCostPayload | null;
        saasCost: PublisherCostPayload | null;
      },
    ) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(id)}/costs`,
        schema: publisherCostsResponseSchema,
        init: jsonInit('PUT', costs),
      }),
  };
}
